// Cache Manager for WITAL Frontend
// Handles localStorage caching with automatic cleanup of expired entries

const CACHE_TTL = 3 * 60 * 60 * 1000 // 3 hours in milliseconds
const CACHE_PREFIX = 'wital_'

// localStorage keys that share the `wital_` prefix but are NOT caches.
// The sweep/clear/prune loops below must skip these — otherwise a
// setLanguage() or setLocation() call (which triggers clearAllCache)
// silently logs the user out by wiping their auth token. Add to this
// set if you introduce a new non-cache wital_-prefixed key.
const PROTECTED_KEYS: ReadonlySet<string> = new Set([
  'wital_token',    // auth JWT — useAuthStore
  'wital_user_id',  // stable per-device id — utils/deviceId
])

function isCacheKey(key: string): boolean {
  return key.startsWith(CACHE_PREFIX) && !PROTECTED_KEYS.has(key)
}

export interface CacheEntry {
  data: any
  timestamp: number
  version: string
}

const CACHE_VERSION = '1.1' // Bumped from 1.0 to force cache invalidation for Build 30

export class CacheManager {
  private static instance: CacheManager

  private constructor() {
    // Clean up expired entries on initialization
    this.cleanupExpiredEntries()
  }

  static getInstance(): CacheManager {
    if (!CacheManager.instance) {
      CacheManager.instance = new CacheManager()
    }
    return CacheManager.instance
  }

  // Clean up expired entries
  private cleanupExpiredEntries(): void {
    const keys = Object.keys(localStorage)
    const witalKeys = keys.filter(isCacheKey)
    const now = Date.now()

    witalKeys.forEach(key => {
      try {
        const cached = JSON.parse(localStorage.getItem(key)!)
        const age = now - cached.timestamp

        if (age > CACHE_TTL) {
          localStorage.removeItem(key)

        }
      } catch {
        // Corrupted entry, remove it
        localStorage.removeItem(key)
      }
    })
  }

  // Remove oldest entries when localStorage quota is exceeded
  private removeOldestEntries(): void {
    const keys = Object.keys(localStorage)
    const witalKeys = keys.filter(isCacheKey)

    if (witalKeys.length === 0) return

    // Sort by timestamp (oldest first)
    const entriesWithTimestamps = witalKeys
      .map(key => {
        try {
          const cached = JSON.parse(localStorage.getItem(key)!)
          return { key, timestamp: cached.timestamp }
        } catch (error) {
          return { key, timestamp: 0 }
        }
      })
      .sort((a, b) => a.timestamp - b.timestamp)

    // Remove oldest 5 entries
    const entriesToRemove = entriesWithTimestamps.slice(0, 5)
    entriesToRemove.forEach(entry => {
      localStorage.removeItem(entry.key)
    })
  }

  // Clear all WITAL cache entries
  clearAllCache(): void {
    const keys = Object.keys(localStorage)
    const witalKeys = keys.filter(isCacheKey)

    witalKeys.forEach(key => {
      localStorage.removeItem(key)
    })
  }

  // Generic get method for any cache key
  get(key: string): any | null {
    const cached = localStorage.getItem(key)
    if (!cached) return null

    try {
      const entry: CacheEntry = JSON.parse(cached)
      const age = Date.now() - entry.timestamp

      if (age > CACHE_TTL) {
        // Expired entry, remove it
        localStorage.removeItem(key)
        return null
      }

      if (entry.version !== CACHE_VERSION) {
        localStorage.removeItem(key)
        return null
      }

      return entry.data
    } catch (error) {
      console.warn('Failed to parse cached data, removing corrupted entry')
      localStorage.removeItem(key)
      return null
    }
  }

  // Generic set method for any cache key
  set(key: string, data: any): void {
    const cacheEntry: CacheEntry = {
      data,
      timestamp: Date.now(),
      version: CACHE_VERSION
    }

    try {
      localStorage.setItem(key, JSON.stringify(cacheEntry))
    } catch (error) {
      console.warn('localStorage quota exceeded, removing oldest entries')
      this.removeOldestEntries()
      localStorage.setItem(key, JSON.stringify(cacheEntry))
    }
  }
}

// Export singleton instance
export const cacheManager = CacheManager.getInstance() 